import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-bg">
      <SiteNav />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="font-display text-4xl font-bold text-textPrimary mb-2">Privacy Policy</h1>
        <p className="text-textSecondary mb-8">Last updated: June 2026</p>

        <div className="prose prose-invert prose-lg max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">1. Introduction</h2>
            <p className="text-textSecondary leading-relaxed">
              At Bankably ("we," "us," "our," or "Company"), we are committed to protecting your privacy and ensuring you have a positive experience on our platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and mobile application.
            </p>
            <p className="text-textSecondary leading-relaxed mt-4">
              Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Services. By accessing and using Bankably, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">2.1 Personal Information You Provide</h3>
            <p className="text-textSecondary leading-relaxed">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Create an account (name, email, phone number, date of birth, address)</li>
              <li>Complete identity verification and KYC (Know Your Customer) procedures</li>
              <li>Make transactions and transfers</li>
              <li>Link bank accounts or payment methods</li>
              <li>Contact our customer support</li>
              <li>Participate in surveys or promotional activities</li>
            </ul>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-textSecondary leading-relaxed">
              When you interact with our platform, we automatically collect:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages visited, time spent, features used)</li>
              <li>Location information (with your consent)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Transaction history and financial activity</li>
            </ul>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">2.3 Information from Third Parties</h3>
            <p className="text-textSecondary leading-relaxed">
              We may receive information about you from:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Financial institutions and payment processors</li>
              <li>Identity verification services</li>
              <li>Credit reporting agencies</li>
              <li>Social media platforms (if you link your accounts)</li>
              <li>Business partners and service providers</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">3. How We Use Your Information</h2>
            <p className="text-textSecondary leading-relaxed">We use the information we collect for various purposes:</p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Providing and improving our Services</li>
              <li>Processing transactions and account management</li>
              <li>Verifying your identity and preventing fraud</li>
              <li>Complying with legal and regulatory requirements</li>
              <li>Communicating with you about account updates and promotions</li>
              <li>Personalizing your experience and content</li>
              <li>Analyzing platform usage and trends</li>
              <li>Enforcing our Terms of Service and other agreements</li>
              <li>Responding to your inquiries and customer support requests</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">4. How We Share Your Information</h2>
            <p className="text-textSecondary leading-relaxed">
              We may share your information with:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li><strong className="text-textPrimary">Service Providers:</strong> Third-party vendors who assist us in operating our platform and conducting our business</li>
              <li><strong className="text-textPrimary">Financial Institutions:</strong> Banks and payment processors to facilitate transactions</li>
              <li><strong className="text-textPrimary">Legal and Law Enforcement:</strong> When required by law or to protect our rights and safety</li>
              <li><strong className="text-textPrimary">Business Partners:</strong> With your consent, to provide integrated services</li>
              <li><strong className="text-textPrimary">Regulatory Authorities:</strong> To comply with financial regulations and reporting requirements</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              We do <strong className="text-textPrimary">not</strong> sell or rent your personal information to third parties for marketing purposes without your explicit consent.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">5. Data Security</h2>
            <p className="text-textSecondary leading-relaxed">
              We implement comprehensive security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>End-to-end encryption for sensitive data transmission</li>
              <li>Secure socket layer (SSL) technology for website communications</li>
              <li>Two-factor authentication and biometric security options</li>
              <li>Regular security audits and penetration testing</li>
              <li>Restricted access to personal information on a need-to-know basis</li>
              <li>Physical, electronic, and procedural safeguards</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              However, no method of transmission over the Internet is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">6. Your Privacy Rights</h2>
            <p className="text-textSecondary leading-relaxed">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li><strong className="text-textPrimary">Access:</strong> The right to access the personal information we hold about you</li>
              <li><strong className="text-textPrimary">Correction:</strong> The right to correct inaccurate or incomplete information</li>
              <li><strong className="text-textPrimary">Deletion:</strong> The right to request deletion of your personal information</li>
              <li><strong className="text-textPrimary">Portability:</strong> The right to receive your data in a structured, commonly used format</li>
              <li><strong className="text-textPrimary">Opt-Out:</strong> The right to opt out of marketing communications and data processing</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              To exercise any of these rights, please contact us at privacy@bankably.co.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-textSecondary leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience. You can control cookie preferences through your browser settings. Disabling cookies may affect the functionality of our platform.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">8. Children's Privacy</h2>
            <p className="text-textSecondary leading-relaxed">
              Our Services are not intended for children under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will promptly delete such information and terminate the child's account.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">9. Contact Us</h2>
            <p className="text-textSecondary leading-relaxed">
              If you have questions or concerns about our Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="bg-surface1 border border-border p-6 rounded-lg mt-4 text-textSecondary">
              <p><strong className="text-textPrimary">Bankably Privacy Team</strong></p>
              <p>Email: privacy@bankably.co</p>
              <p>Address: 123 Financial Street, New York, NY 10001, USA</p>
            </div>
          </section>

          {/* Policy Updates */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-textSecondary leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of significant changes by updating the "Last Updated" date and, where appropriate, by sending you an email notification. Your continued use of our Services constitutes your acceptance of the updated Privacy Policy.
            </p>
          </section>
        </div>

        {/* Footer Link */}
        <div className="mt-16 pt-8 border-t border-border">
          <Link to="/" className="text-gold hover:text-goldLight font-semibold">
            ← Back to Home
          </Link>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
